import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { ReportSection } from '@/components/reports/ReportSection'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

export function MarginReportPanel() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])

  async function load() {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.get('/reports/margin')
      const payload = data?.data ?? data ?? {}
      setRows(payload.rows ?? payload.items ?? payload.products ?? [])
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load margin report.'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  return (
    <ReportSection
      title="Profit & margin"
      description="Cost vs sell on products and services (simple margin)."
      actions={
        <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Cost</th>
                <th className="px-3 py-2">Sell</th>
                <th className="px-3 py-2">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-muted-foreground">
                    No margin rows yet.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={`${row.type}-${row.id ?? row.name}`} className="border-t">
                    <td className="px-3 py-2">{row.name ?? row.label}</td>
                    <td className="px-3 py-2 capitalize">{row.type ?? '—'}</td>
                    <td className="px-3 py-2">{Number(row.cost ?? row.cost_price ?? 0).toFixed(2)}</td>
                    <td className="px-3 py-2">{Number(row.sell ?? row.sell_price ?? row.price ?? 0).toFixed(2)}</td>
                    <td className="px-3 py-2">{Number(row.margin_pct ?? row.margin ?? 0).toFixed(1)}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </ReportSection>
  )
}
